/**
 * Environment Configuration
 * Centralized environment-based configuration management
 */

export type Environment = 'development' | 'staging' | 'production';

interface EnvironmentConfig {
  env: Environment;
  isDevelopment: boolean;
  isProduction: boolean;
  isStaging: boolean;
  firebase: {
    apiKey: string;
    authDomain: string;
    projectId: string;
    storageBucket: string;
    messagingSenderId: string;
    appId: string;
    databaseId: string;
  };
  features: {
    enableConsoleLogs: boolean;
    enableDebugTools: boolean;
    enableErrorReporting: boolean;
    enableAnalytics: boolean;
  };
  api: {
    timeout: number;
    retryAttempts: number;
  };
  monitoring: {
    sentryDsn?: string;
    logRocketId?: string;
  };
}

class EnvironmentService {
  private config: EnvironmentConfig;

  constructor() {
    const env = (process.env.REACT_APP_ENV || process.env.NODE_ENV || 'development') as Environment;

    this.config = {
      env,
      isDevelopment: env === 'development',
      isProduction: env === 'production',
      isStaging: env === 'staging',
      firebase: {
        apiKey: process.env.REACT_APP_FIREBASE_API_KEY || '',
        authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN || '',
        projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID || '',
        storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET || '',
        messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID || '',
        appId: process.env.REACT_APP_FIREBASE_APP_ID || '',
        databaseId: process.env.REACT_APP_FIREBASE_DATABASE_ID || '(default)',
      },
      features: {
        enableConsoleLogs: process.env.REACT_APP_ENABLE_CONSOLE_LOGS === 'true' || env === 'development',
        enableDebugTools: process.env.REACT_APP_ENABLE_DEBUG_TOOLS === 'true' || env === 'development',
        enableErrorReporting: process.env.REACT_APP_ENABLE_ERROR_REPORTING === 'true' || env === 'production',
        enableAnalytics: process.env.REACT_APP_ENABLE_ANALYTICS === 'true' || env === 'production',
      },
      api: {
        timeout: parseInt(process.env.REACT_APP_API_TIMEOUT || '30000', 10),
        retryAttempts: parseInt(process.env.REACT_APP_API_RETRY_ATTEMPTS || '3', 10),
      },
      monitoring: {
        sentryDsn: process.env.REACT_APP_SENTRY_DSN,
        logRocketId: process.env.REACT_APP_LOGROCKET_ID,
      },
    };

    this.validateConfig();
  }

  /**
   * Validate required configuration
   */
  private validateConfig() {
    const requiredFirebaseKeys: (keyof EnvironmentConfig['firebase'])[] = [
      'apiKey',
      'authDomain',
      'projectId',
      'storageBucket',
      'messagingSenderId',
      'appId',
    ];

    const missingKeys = requiredFirebaseKeys.filter(
      key => !this.config.firebase[key]
    );

    if (missingKeys.length > 0 && this.config.isProduction) {
      throw new Error(
        `Missing required Firebase configuration in production: ${missingKeys.join(', ')}`
      );
    }
  }

  /**
   * Get current environment
   */
  getEnvironment(): Environment {
    return this.config.env;
  }

  /**
   * Check if development
   */
  isDevelopment(): boolean {
    return this.config.isDevelopment;
  }

  /**
   * Check if production
   */
  isProduction(): boolean {
    return this.config.isProduction;
  }

  /**
   * Check if staging
   */
  isStaging(): boolean {
    return this.config.isStaging;
  }

  /**
   * Get Firebase configuration
   */
  getFirebaseConfig() {
    return this.config.firebase;
  }

  /**
   * Check if feature is enabled
   */
  isFeatureEnabled(feature: keyof EnvironmentConfig['features']): boolean {
    return this.config.features[feature];
  }

  /**
   * Get API configuration
   */
  getApiConfig() {
    return this.config.api;
  }

  /**
   * Get monitoring configuration
   */
  getMonitoringConfig() {
    return this.config.monitoring;
  }

  /**
   * Get full configuration (for debugging)
   */
  getConfig(): EnvironmentConfig {
    return { ...this.config };
  }

  /**
   * Print configuration summary (development only)
   */
  printConfigSummary() {
    if (!this.isDevelopment()) return;
  }
}

export const envConfig = new EnvironmentService();
